import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class TwaDetectionService {
  private isTwaCache: boolean | null = null;
  private isChromeOsPwaCache: boolean | null = null;

  /**
   * Detection methods:
   * 1. Check for 'display-mode: standalone' in CSS media query (PWA installed)
   * 2. Check for Digital Goods API (only available in TWA)
   * 3. Check user agent for TWA indicators
   * @returns true if running as TWA from Play Store, false otherwise
   */
  isTrustedWebActivity(): boolean {
    // Return cached result if available
    if (this.isTwaCache !== null) {
      return this.isTwaCache;
    }

    // Check if running in browser environment
    if (typeof window === 'undefined') {
      this.isTwaCache = false;
      return false;
    }

    // Method 1: Check if running in standalone mode (PWA)
    const isStandalone = this.isRunningStandalone();

    // Method 2: Check for Digital Goods API (only available in TWA)
    const hasDigitalGoodsApi = this.hasDigitalGoodsApi();

    // Method 3: Check user agent for TWA indicators
    const userAgent = navigator.userAgent.toLowerCase();
    const hasTwaIndicators =
      userAgent.includes('wv') || // WebView indicator
      userAgent.includes('android') ||
      userAgent.includes('cros'); // Chrome OS

    const isTwa = isStandalone && hasDigitalGoodsApi && hasTwaIndicators;

    // Cache the result
    this.isTwaCache = isTwa;

    this.log('[TwaDetection] TWA Detection:', { isTwa, isStandalone, hasDigitalGoodsApi, hasTwaIndicators });

    return isTwa;
  }

  /**
   * Logs messages only in non-production environments.
   */
  private log(...args: any[]): void {
    if (!environment.production) {
      console.log(...args);
    }
  }

  /**
   * Checks if running on ChromeOS as a PWA (TWA) from Play Store.
   * This is the recommended method for Google Play Billing checks.
   *
   * @returns true if running on ChromeOS as TWA
   */
  isChromeOsPwa(): boolean {
    // Return cached result if available
    if (this.isChromeOsPwaCache !== null) {
      return this.isChromeOsPwaCache;
    }

    const isChromeOS = this.isChromeOS();
    const isTwa = this.isTrustedWebActivity();
    const result = isChromeOS && isTwa;

    // Cache the result
    this.isChromeOsPwaCache = result;

    this.log('[TwaDetection] ChromeOS PWA check:', { isChromeOS, isTwa, result });

    return result;
  }

  /**
   * Checks if Google Play Billing is available and can be used.
   * Combines TWA detection with Digital Goods API check.
   *
   * @returns true if Google Play Billing is available
   */
  canUseGooglePlayBilling(): boolean {
    return this.isTrustedWebActivity() && this.hasDigitalGoodsApi();
  }

  /**
   * Clears the detection cache. Useful for testing or when
   * environment changes need to be re-evaluated.
   */
  clearCache(): void {
    this.isTwaCache = null;
    this.isChromeOsPwaCache = null;
  }

  /**
   * Checks if the app is running in standalone mode (installed as PWA).
   * This includes both TWA and browser-installed PWAs.
   *
   * @returns true if running in standalone mode
   */
  private isRunningStandalone(): boolean {
    // Check display-mode media query
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }

    // Fallback: Check navigator.standalone (iOS Safari)
    if ((navigator as any).standalone === true) {
      return true;
    }

    // Fallback: Check if launched from home screen (Android)
    if (window.matchMedia('(display-mode: minimal-ui)').matches) {
      return true;
    }

    return false;
  }

  /**
   * Checks if running on Chrome OS (Chromebook).
   *
   * @returns true if running on Chrome OS
   */
  isChromeOS(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('cros');
  }

  /**
   * Checks if the Digital Goods API is available.
   * This API is only available in TWA environments.
   *
   * @returns true if Digital Goods API is available
   */
  hasDigitalGoodsApi(): boolean {
    return typeof window !== 'undefined' && 'getDigitalGoodsService' in window;
  }

  /**
   * Gets detailed environment information for debugging.
   *
   * @returns object with environment details
   */
  getEnvironmentInfo(): {
    isTwa: boolean;
    isStandalone: boolean;
    isChromeOS: boolean;
    hasDigitalGoodsApi: boolean;
    userAgent: string;
    displayMode: string;
  } {
    return {
      isTwa: this.isTrustedWebActivity(),
      isStandalone: this.isRunningStandalone(),
      isChromeOS: this.isChromeOS(),
      hasDigitalGoodsApi: this.hasDigitalGoodsApi(),
      userAgent: navigator.userAgent,
      displayMode: this.getDisplayMode(),
    };
  }

  /**
   * Gets the current display mode.
   *
   * @returns display mode string
   */
  private getDisplayMode(): string {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return 'standalone';
    }
    if (window.matchMedia('(display-mode: minimal-ui)').matches) {
      return 'minimal-ui';
    }
    if (window.matchMedia('(display-mode: fullscreen)').matches) {
      return 'fullscreen';
    }
    return 'browser';
  }
}
